/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { Order } from "@lemmonhq/types";
import { getOrderFoodTypeStatusColor } from "@app/utils/index";
import { getOrderStatusButtonText, getNextOrderStatus } from "./helpers";
import { api } from "@app/data/api";
import { useLocalization } from "@lemmonhq/uikit";

interface OrderCardOrderStatusButtonProps {
  order: Order;
  onCloseOrder?: (order: Order) => Promise<boolean>;
  loading?: boolean;
  onOrderUpdate: (order: Order) => void;
}

export const OrderCardOrderStatusButton =
  defineComponent<OrderCardOrderStatusButtonProps>({
    name: "OrderCardOrderStatusButton",
    props: ["order", "onCloseOrder", "loading", "onOrderUpdate"],
    setup(props) {
      const { t } = useLocalization();
      const isLoading = ref(false);

      const handleStatusChange = async () => {
        console.log("handleStatusChange", isLoading.value);
        if (isLoading.value) return;

        isLoading.value = true;
        try {
          // Special handling for ready orders without table_id
          if (props.order.status === "ready" && props.onCloseOrder) {
            console.log("close");
            await props.onCloseOrder(props.order);
          } else {
            const { orders } = await api.changeOrderStatus({
              type: "order",
              orderID: props.order.id,
              foodTypeId: undefined,
              currentStatus: props.order.status,
              orderStatus: getNextOrderStatus(props.order.status),
            });
            await props.onOrderUpdate(orders[0]);
            console.log("should change status");
          }
        } catch (e) {
          console.log("e", e);
        } finally {
          isLoading.value = false;
        }
      };

      const loadingNode = (
        <span class="flex items-center justify-center">
          <span class="loading loading-xs loading-spinner loading mr-2"></span>
        </span>
      );

      return () => {
        const { order } = props;
        const loading = props.loading || isLoading.value;

        // Always show status button for all statuses except "new" (which uses PendingOrderCard)
        if (order.service_method === "dine-in" && order.status !== "ready") {
          //return null;
        }
        if (order.table_id) return null;

        // For ready orders without table_id, always show close button
        const isReadyTakeaway = order.status === "ready" && !order.table_id;

        const disabled = loading;

        // Use special styling for close button
        const buttonText = isReadyTakeaway
          ? t("orders.status.close")
          : getOrderStatusButtonText(order.status, t);
        const buttonStyle = isReadyTakeaway
          ? "background: var(--order-card-status-completed-background); color: var(--order-card-status-completed-content);"
          : getOrderFoodTypeStatusColor(order.status);

        return (
          <button
            data-a={order.service_method}
            style={buttonStyle}
            onClick={handleStatusChange}
            disabled={disabled}
            data-status={order.status}
            class={`${
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } text-center font-semibold px-3 py-1 rounded mb-2 w-full`}
          >
            {loading ? loadingNode : buttonText}
          </button>
        );
      };
    },
  });
